/**
 * Pure helpers for the desktop claim progress rail: route constants, query parsing, and step derivation.
 * @module claims/claimFlowModel
 */

import type { SingleVehicleCollisionTargetId } from './claimDraftStorage'
import {
  readOtherPartyDriverFromDraft,
  type MultiVehicleOtherPartyRailSummaryInput,
} from './multiVehicleOtherPartyDrivers'
import {
  formatOtherClaimSubtypeSummary,
  type OtherClaimSubtypeId,
} from './otherClaimSubtypeOptions'
import type { PolicyLink } from '../services/types'
import { shortVehicleFromPayload, shortVehicleDisplay } from '../utils/policyDisplay'

export type { OtherClaimSubtypeId }

/** Query key for {@link OtherClaimSubtypeId} on non–windscreen routes after subtype is chosen. */
export const OTHER_CLAIM_SUBTYPE_QUERY_KEY = 'claimSubtype'

/** Routes that participate in the desktop claim progress rail. */
export const CLAIM_FLOW_PATH = {
  start: '/claims/start',
  selectType: '/claims/select-type',
  /**
   * Non–windscreen: card list for incident subtype (collision, theft, etc.) before when & where.
   */
  otherClaimSubtype: '/claims/other/subtype',
  /** When & where the incident occurred (after windscreen vs other is chosen; before GST or towing). */
  incident: '/claims/incident',
  /** Legacy non–windscreen GST route; redirects to towing or subtype driver (GST is windscreen-only). */
  otherGst: '/claims/other/gst',
  /**
   * Single-vehicle collision only: who was driving (after when & where; GST is collected on review, like multi-vehicle).
   */
  otherSingleVehicleDriver: '/claims/other/single-vehicle/driver',
  /** Single-vehicle only: what the vehicle collided with (after driver step). */
  otherSingleVehicleCollisionWith: '/claims/other/single-vehicle/collision-with',
  /** Single-vehicle only: incident + damage narratives (before review / GST on review). */
  otherSingleVehicleDescribe: '/claims/other/single-vehicle/describe',
  /** Single-vehicle only: confirm & submit. */
  otherSingleVehicleReview: '/claims/other/single-vehicle/review',
  otherSingleVehicleSubmitted: '/claims/other/single-vehicle/submitted',
  /**
   * Multi-vehicle collision only: driver at collision (after GST; replaces towing for this subtype).
   */
  otherMultiVehicleDriver: '/claims/other/multi-vehicle/driver',
  /**
   * Multi-vehicle collision only: optional other-involved driver details (after insured driver step; before further details).
   */
  otherMultiVehicleOtherParty: '/claims/other/multi-vehicle/other-party',
  /** Multi-vehicle collision only: incident + damage narratives (before review / GST on review). */
  otherMultiVehicleDescribe: '/claims/other/multi-vehicle/describe',
  /** Multi-vehicle collision only: confirm & submit (mirrors windscreen review). */
  otherMultiVehicleReview: '/claims/other/multi-vehicle/review',
  otherMultiVehicleSubmitted: '/claims/other/multi-vehicle/submitted',
  /** Weather: insured driver (same capture as single-vehicle; dedicated URL). */
  otherWeatherDriver: '/claims/other/weather/driver',
  /** Weather: incident narrative + weather conditions before confirm. */
  otherWeatherDescribe: '/claims/other/weather/describe',
  /** Weather: confirm & submit. */
  otherWeatherReview: '/claims/other/weather/review',
  otherWeatherSubmitted: '/claims/other/weather/submitted',
  /** Fire: insured driver (same capture as weather; dedicated URL). */
  otherFireDriver: '/claims/other/fire/driver',
  /** Fire: incident narrative + type of fire before confirm. */
  otherFireDescribe: '/claims/other/fire/describe',
  /** Fire: confirm & submit. */
  otherFireReview: '/claims/other/fire/review',
  otherFireSubmitted: '/claims/other/fire/submitted',
  /** Theft: insured driver (same capture as fire; dedicated URL). */
  otherTheftDriver: '/claims/other/theft/driver',
  /** Theft: incident narrative + theft/break-in detail before confirm. */
  otherTheftDescribe: '/claims/other/theft/describe',
  /** Theft: confirm & submit. */
  otherTheftReview: '/claims/other/theft/review',
  otherTheftSubmitted: '/claims/other/theft/submitted',
  /** Malicious damage: incident + damage narratives before confirm (no driver step). */
  otherMaliciousDamageDescribe: '/claims/other/malicious-damage/describe',
  otherMaliciousDamageReview: '/claims/other/malicious-damage/review',
  otherMaliciousDamageSubmitted: '/claims/other/malicious-damage/submitted',
  /** Other loss: incident + damage narratives before confirm (no driver step). */
  otherOtherLossDescribe: '/claims/other/other-loss/describe',
  otherOtherLossReview: '/claims/other/other-loss/review',
  otherOtherLossSubmitted: '/claims/other/other-loss/submitted',
  windscreen: '/claims/windscreen',
  /** Legacy windscreen GST route; GST / ITC is collected on the review step and this URL redirects. */
  windscreenGst: '/claims/windscreen/gst',
  /** Windscreen branch: chip and replacement questions (after describe). */
  windscreenDetail: '/claims/windscreen/detail',
  /** Windscreen branch: trusted vs own repairer (after further detail). */
  windscreenRepairOption: '/claims/windscreen/repair-option',
  /** Windscreen branch: review & confirm before submit. */
  windscreenReview: '/claims/windscreen/review',
  windscreenSubmitted: '/claims/windscreen/submitted',
  towing: '/claims/towing',
  /** Placeholder step after towing is answered (`?towing=yes|no`). */
  details: '/claims/details',
} as const

/** React Router location state shared across claim flow steps. */
export type ClaimFlowLocationState = {
  policy?: PolicyLink
  preserveScroll?: boolean
  /** When set, fire/weather/theft driver must not auto-advance to describe (user tapped Back). */
  preventDriverAutoAdvance?: boolean
}

/** Location state for navigating Back from describe to the driver step. */
export function claimFlowNavStateForDriverBack(policy: PolicyLink | undefined): ClaimFlowLocationState {
  return {
    ...(policy ? { policy } : {}),
    preventDriverAutoAdvance: true,
  }
}

/** Subtypes that skip legacy `/claims/towing` (all except off-road). GST is windscreen-only. */
export function otherSubtypeSkipsLegacyGstAndTowing(subtype: OtherClaimSubtypeId | null): boolean {
  return (
    subtype === 'multi-vehicle-collision' ||
    subtype === 'single-vehicle' ||
    subtype === 'weather' ||
    subtype === 'fire' ||
    subtype === 'theft'
  )
}

/** Parses `towing=` from claim URLs (`yes` | `no`). */
export function parseTowingAnswerQuery(raw: string | null): 'yes' | 'no' | null {
  if (raw === 'yes' || raw === 'no') return raw
  return null
}

/** Branch chosen on claim type screen (`windscreen` | `other`). */
export type ClaimEntryBranch = 'windscreen' | 'other'

/** Query key for {@link ClaimEntryBranch} on `/claims/incident` (after user picks windscreen vs other). */
export const CLAIM_BRANCH_QUERY_KEY = 'branch'

/** Parses `branch=` from claim URLs after type selection. */
export function parseClaimBranchQuery(raw: string | null): ClaimEntryBranch | null {
  if (raw === 'windscreen' || raw === 'other') return raw
  return null
}

/** Builds `/claims/other/subtype` href (policy hints only; no branch query). */
export function buildClaimOtherSubtypeHref(
  policyRef: string | null,
  policyTypeQuery: string | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherClaimSubtype}?${q}` : CLAIM_FLOW_PATH.otherClaimSubtype
}

/** Builds `/claims/other/gst` href including {@link OTHER_CLAIM_SUBTYPE_QUERY_KEY} when known. */
export function buildClaimOtherGstHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherGst}?${q}` : CLAIM_FLOW_PATH.otherGst
}

/** Builds `/claims/towing` href including {@link OTHER_CLAIM_SUBTYPE_QUERY_KEY} when known. */
export function buildClaimOtherTowingHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.towing}?${q}` : CLAIM_FLOW_PATH.towing
}

/** Builds `/claims/other/single-vehicle/driver` href (same query shape as other GST). */
export function buildClaimOtherSingleVehicleDriverHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherSingleVehicleDriver}?${q}` : CLAIM_FLOW_PATH.otherSingleVehicleDriver
}

/** Builds `/claims/other/single-vehicle/collision-with` href (same query shape as other GST). */
export function buildClaimOtherSingleVehicleCollisionWithHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q
    ? `${CLAIM_FLOW_PATH.otherSingleVehicleCollisionWith}?${q}`
    : CLAIM_FLOW_PATH.otherSingleVehicleCollisionWith
}

/** Builds `/claims/other/single-vehicle/describe` href (same query shape as other single-vehicle routes). */
export function buildClaimOtherSingleVehicleDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherSingleVehicleDescribe}?${q}` : CLAIM_FLOW_PATH.otherSingleVehicleDescribe
}

/** Builds `/claims/other/single-vehicle/review` href (same query shape as other single-vehicle routes). */
export function buildClaimOtherSingleVehicleReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherSingleVehicleReview}?${q}` : CLAIM_FLOW_PATH.otherSingleVehicleReview
}

/** Builds `/claims/other/multi-vehicle/driver` href (same query shape as other GST). */
export function buildClaimOtherMultiVehicleDriverHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMultiVehicleDriver}?${q}` : CLAIM_FLOW_PATH.otherMultiVehicleDriver
}

/** Builds `/claims/other/multi-vehicle/other-party` href (same query shape as other multi-vehicle routes). */
export function buildClaimOtherMultiVehicleOtherPartyHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMultiVehicleOtherParty}?${q}` : CLAIM_FLOW_PATH.otherMultiVehicleOtherParty
}

/** Builds `/claims/other/multi-vehicle/describe` href (same query shape as other multi-vehicle routes). */
export function buildClaimOtherMultiVehicleDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMultiVehicleDescribe}?${q}` : CLAIM_FLOW_PATH.otherMultiVehicleDescribe
}

/** Builds `/claims/other/multi-vehicle/review` href (same query shape as other multi-vehicle routes). */
export function buildClaimOtherMultiVehicleReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMultiVehicleReview}?${q}` : CLAIM_FLOW_PATH.otherMultiVehicleReview
}

/** Builds `/claims/other/weather/driver` href (same query shape as other multi-vehicle routes). */
export function buildClaimOtherWeatherDriverHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherWeatherDriver}?${q}` : CLAIM_FLOW_PATH.otherWeatherDriver
}

/** Builds `/claims/other/weather/describe` href. */
export function buildClaimOtherWeatherDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherWeatherDescribe}?${q}` : CLAIM_FLOW_PATH.otherWeatherDescribe
}

/** Builds `/claims/other/weather/review` href. */
export function buildClaimOtherWeatherReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherWeatherReview}?${q}` : CLAIM_FLOW_PATH.otherWeatherReview
}

/** Builds `/claims/other/fire/driver` href (same query shape as weather routes). */
export function buildClaimOtherFireDriverHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherFireDriver}?${q}` : CLAIM_FLOW_PATH.otherFireDriver
}

/** Builds `/claims/other/fire/describe` href. */
export function buildClaimOtherFireDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherFireDescribe}?${q}` : CLAIM_FLOW_PATH.otherFireDescribe
}

/** Builds `/claims/other/fire/review` href. */
export function buildClaimOtherFireReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherFireReview}?${q}` : CLAIM_FLOW_PATH.otherFireReview
}

/** Builds `/claims/other/theft/driver` href (same query shape as fire routes). */
export function buildClaimOtherTheftDriverHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherTheftDriver}?${q}` : CLAIM_FLOW_PATH.otherTheftDriver
}

/** Builds `/claims/other/theft/describe` href. */
export function buildClaimOtherTheftDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherTheftDescribe}?${q}` : CLAIM_FLOW_PATH.otherTheftDescribe
}

/** Builds `/claims/other/theft/review` href. */
export function buildClaimOtherTheftReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherTheftReview}?${q}` : CLAIM_FLOW_PATH.otherTheftReview
}

/** Builds `/claims/other/malicious-damage/describe` href. */
export function buildClaimOtherMaliciousDamageDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMaliciousDamageDescribe}?${q}` : CLAIM_FLOW_PATH.otherMaliciousDamageDescribe
}

/** Builds `/claims/other/malicious-damage/review` href. */
export function buildClaimOtherMaliciousDamageReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherMaliciousDamageReview}?${q}` : CLAIM_FLOW_PATH.otherMaliciousDamageReview
}

/** Builds `/claims/other/other-loss/describe` href. */
export function buildClaimOtherOtherLossDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherOtherLossDescribe}?${q}` : CLAIM_FLOW_PATH.otherOtherLossDescribe
}

/** Builds `/claims/other/other-loss/review` href. */
export function buildClaimOtherOtherLossReviewHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.otherOtherLossReview}?${q}` : CLAIM_FLOW_PATH.otherOtherLossReview
}

/** Three-step other branches: when/where → describe → review (no driver or towing). */
export function buildClaimOtherSimpleBranchDescribeHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId
): string {
  if (claimSubtype === 'malicious-damage') {
    return buildClaimOtherMaliciousDamageDescribeHref(policyRef, policyTypeQuery, claimSubtype)
  }
  if (claimSubtype === 'other-loss') {
    return buildClaimOtherOtherLossDescribeHref(policyRef, policyTypeQuery, claimSubtype)
  }
  return buildClaimOtherFireDescribeHref(policyRef, policyTypeQuery, claimSubtype)
}

/** Builds `/claims/details` with policy / type / subtype query hints. */
export function buildClaimDetailsHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  claimSubtype: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  const q = p.toString()
  return q ? `${CLAIM_FLOW_PATH.details}?${q}` : CLAIM_FLOW_PATH.details
}

/** Builds `/claims/incident` href with policy hints and the chosen claim branch. */
export function buildClaimIncidentHref(
  policyRef: string | null,
  policyTypeQuery: string | null,
  branch: ClaimEntryBranch,
  claimSubtype?: OtherClaimSubtypeId | null
): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (policyTypeQuery) p.set('type', policyTypeQuery)
  p.set(CLAIM_BRANCH_QUERY_KEY, branch)
  if (branch === 'other' && claimSubtype) {
    p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
  }
  return `${CLAIM_FLOW_PATH.incident}?${p.toString()}`
}

export type ClaimFlowStepStatus = 'complete' | 'current' | 'upcoming'

export interface ClaimFlowStepView {
  id: string
  title: string
  /** Short human-readable line (answers so far), optional per step */
  summary?: string
  status: ClaimFlowStepStatus
}

export interface ClaimFlowSnapshot {
  pathname: string
  /** Best-effort policy / vehicle line for the rail */
  policySummary?: string | null
  towingAnswer?: 'yes' | 'no' | null
  /** Windscreen branch: full description text; rail shows a truncated preview */
  windscreenDescription?: string | null
  /** Windscreen branch: small-chip yes/no for the rail summary */
  windscreenSmallChip?: boolean | null
  /** Windscreen branch: already repaired/replaced; skips repair-option step when true */
  windscreenReplacedAlready?: boolean | null
  /** Windscreen branch: repair option for the rail summary */
  windscreenRepairOption?: 'trusted' | 'own' | null
  /** Windscreen branch: GST registration answer for the rail */
  windscreenGstRegistered?: boolean | null
  /** Windscreen branch: ABN digits when provided */
  windscreenAbn?: string | null
  /** Windscreen branch: ITC entitlement when GST-registered */
  windscreenItcEntitled?: boolean | null
  /** Windscreen branch: ITC percentage 0–100 when entitled */
  windscreenItcPercentage?: number | null
  /** After type selection: windscreen vs other (for incident + rail). */
  claimEntryBranch?: ClaimEntryBranch | null
  /** Single-line summary for the incident date/time/location step on the rail. */
  incidentSummary?: string | null
  /** Non–windscreen: selected subtype id for rail summary (must match URL / draft). */
  otherClaimSubtypeId?: OtherClaimSubtypeId | null
  /** Multi-vehicle collision: short line for the driver-at-collision step on the rail. */
  multiVehicleCollisionSummary?: string | null
  /** Multi-vehicle collision: short line for the other-driver step on the rail. */
  multiVehicleOtherPartySummary?: string | null
  /** Multi-vehicle collision: incident narrative for the describe-step rail preview. */
  multiVehicleIncidentDescription?: string | null
  /** Multi-vehicle collision: damage narrative for the describe-step rail preview. */
  multiVehicleDamageDescription?: string | null
  /**
   * Legacy single describe field (pre-split). Still merged into the rail preview until the user saves
   * the describe step again.
   */
  multiVehicleClaimDescription?: string | null
  /** Weather: short line for the weather-conditions field on the rail (optional). */
  weatherClaimConditionsDescription?: string | null
  /** Fire: short line for “type of fire” on the rail (optional). */
  fireClaimTypeDescription?: string | null
  /** Theft: short line for theft/break-in detail on the rail (optional). */
  theftOrBreakInDetailDescription?: string | null
  /** Single-vehicle: short line for the collision-target step on the rail. */
  singleVehicleCollisionSummary?: string | null
  /** Single-vehicle collision: incident narrative for the describe-step rail preview. */
  singleVehicleIncidentDescription?: string | null
  /** Single-vehicle collision: damage narrative for the describe-step rail preview. */
  singleVehicleDamageDescription?: string | null
  /** Weather driver step: parked at incident (rail summary only). */
  weatherVehicleParkedAtIncident?: boolean | null
  /** Fire driver step: parked at incident (rail summary only). */
  fireVehicleParkedAtIncident?: boolean | null
  /** Theft driver step: parked at incident (rail summary only). */
  theftVehicleParkedAtIncident?: boolean | null
}

/** Strip trailing slash for stable matching against {@link CLAIM_FLOW_PATH} constants. */
function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1)
  }
  return pathname
}

/**
 * Build a single line for policy context (vehicle name preferred, else policy ref).
 * Prefers a short "Year Make Model" form from structured fields to avoid truncation.
 */
export function formatClaimPolicySummaryLine(
  input: {
    policyRef?: string | null
    vehicleName?: string | null
    yearBuilt?: string | null
    make?: string | null
    model?: string | null
  } | null | undefined
): string | undefined {
  if (!input) return undefined
  const fromPayload = shortVehicleFromPayload(input.yearBuilt, input.make, input.model)
  if (fromPayload) return fromPayload
  const vehicle = input.vehicleName?.trim()
  if (vehicle) return shortVehicleDisplay(vehicle) || vehicle
  const ref = input.policyRef?.trim()
  if (ref) return `Policy ${ref}`
  return undefined
}

/** Mirrors primary option headline when user chose windscreen (see ClaimTypeSelection). */
export const CLAIM_FLOW_SUMMARY_WINDSCREEN = 'Windscreen or glass'
export const CLAIM_FLOW_SUMMARY_OTHER_CLAIM = 'non-windscreen'

/** Truncated preview for free-text description steps on the progress rail (windscreen + multi-vehicle). */
export function formatClaimDescriptionRailSummary(text: string | null | undefined): string | undefined {
  const t = text?.trim()
  if (!t) return undefined
  return t.length > 48 ? `${t.slice(0, 45)}…` : t
}

/**
 * Single-line rail preview for the multi-vehicle describe step (incident + damage, plus legacy blob when present).
 */
export function formatMultiVehicleDescribeRailSummary(
  input: Pick<
    ClaimFlowSnapshot,
    'multiVehicleIncidentDescription' | 'multiVehicleDamageDescription' | 'multiVehicleClaimDescription'
  > | null
): string | undefined {
  if (!input) return undefined
  const incident =
    input.multiVehicleIncidentDescription?.trim() || input.multiVehicleClaimDescription?.trim() || ''
  const damage = input.multiVehicleDamageDescription?.trim() || ''
  const combined = [incident, damage].filter(Boolean).join(' · ')
  if (!combined) return undefined
  return formatClaimDescriptionRailSummary(combined)
}

/**
 * Single-line rail preview for the single-vehicle describe step (incident + damage).
 */
export function formatSingleVehicleDescribeRailSummary(
  input: Pick<ClaimFlowSnapshot, 'singleVehicleIncidentDescription' | 'singleVehicleDamageDescription'> | null
): string | undefined {
  if (!input) return undefined
  const incident = input.singleVehicleIncidentDescription?.trim() || ''
  const damage = input.singleVehicleDamageDescription?.trim() || ''
  const combined = [incident, damage].filter(Boolean).join(' · ')
  if (!combined) return undefined
  return formatClaimDescriptionRailSummary(combined)
}

/** Rail summary for windscreen GST / ABN / ITC step. */
export function windscreenGstRailSummary(
  registered: boolean | null | undefined,
  abn: string | null | undefined,
  itcEntitled?: boolean | null | undefined,
  itcPercentage?: number | null | undefined
): string | undefined {
  if (registered === true) {
    const digits = abn?.replace(/\D/g, '') ?? ''
    let line = digits.length === 11 ? `GST registered · ABN ${digits}` : 'GST registered: Yes'
    if (itcEntitled === true) {
      if (
        typeof itcPercentage === 'number' &&
        Number.isInteger(itcPercentage) &&
        itcPercentage >= 0 &&
        itcPercentage <= 100
      ) {
        line += ` · ITC ${itcPercentage}%`
      } else {
        line += ' · ITC: Yes'
      }
    } else if (itcEntitled === false) {
      line += ' · ITC: No'
    }
    return line.length > 52 ? `${line.slice(0, 49)}…` : line
  }
  if (registered === false) return 'Not registered for GST'
  return undefined
}

/**
 * Compact line for the incident step on the desktop progress rail.
 */
export function formatIncidentRailSummary(fields: {
  incidentDateDdMmYyyy?: string | null
  incidentTimeHm?: string | null
  incidentTimeAmPm?: 'AM' | 'PM' | null
  incidentAddressSearch?: string | null
  incidentStreet?: string | null
  incidentSuburb?: string | null
  incidentState?: string | null
  incidentPostcode?: string | null
  incidentAddressUnknown?: boolean | null
} | null | undefined): string | undefined {
  if (!fields) return undefined
  const date = fields.incidentDateDdMmYyyy?.trim()
  const time = fields.incidentTimeHm?.trim()
  const ampm = fields.incidentTimeAmPm
  const parts: string[] = []
  if (date) parts.push(date)
  if (time && ampm) parts.push(`${time} ${ampm}`)
  else if (time) parts.push(time)
  if (fields.incidentAddressUnknown) {
    const search = fields.incidentAddressSearch?.trim()
    const stt = fields.incidentState?.trim()
    if (search) {
      const withState =
        stt && !stateMentionedInLocationText(search, stt) ? `${search}, ${stt}` : search
      parts.push(withState)
    } else if (stt) {
      parts.push(stt)
    } else {
      parts.push('Location unknown')
    }
  } else {
    const loc = [fields.incidentSuburb?.trim(), fields.incidentState?.trim()].filter(Boolean).join(', ')
    if (loc) parts.push(loc)
  }
  if (parts.length === 0) return undefined
  const line = parts.join(' · ')
  return line.length > 52 ? `${line.slice(0, 49)}…` : line
}

/** Fields needed for incident summary rows on review screens (same shape as draft subset). */
export type IncidentReviewFields = Parameters<typeof formatIncidentRailSummary>[0]

/**
 * Human-readable date and time for claim review (full sentence-friendly line).
 */
export function formatIncidentReviewDateTime(fields: IncidentReviewFields): string {
  if (!fields) return 'Not provided'
  const date = fields.incidentDateDdMmYyyy?.trim()
  const time = fields.incidentTimeHm?.trim()
  const ampm = fields.incidentTimeAmPm
  const timePart = time ? (ampm ? `${time} ${ampm}` : time) : ''
  if (!date && !timePart) return 'Not provided'
  if (date && timePart) return `${date}, ${timePart}`
  return date || timePart
}

/** True when `state` already appears as a whole token in free-text location (e.g. NSW in “near Ballina NSW”). */
function stateMentionedInLocationText(text: string, state: string): boolean {
  if (!state.trim()) return false
  return new RegExp(`\\b${state.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text)
}

/**
 * Location line when the portal user flagged “don’t know the exact address”.
 * Never surfaces structured street/suburb/postcode (even if stale draft data exists).
 */
function formatUnknownIncidentReviewLocation(fields: NonNullable<IncidentReviewFields>): string {
  const search = fields.incidentAddressSearch?.trim()
  const state = fields.incidentState?.trim()
  let detail = ''
  if (search) {
    detail = state && !stateMentionedInLocationText(search, state) ? `${search}, ${state}` : search
  } else if (state) {
    detail = state
  }
  return detail ? `Exact address unknown — ${detail}` : 'Exact address unknown'
}

/**
 * Human-readable location for claim review from manual address fields or “unknown”.
 */
export function formatIncidentReviewLocation(fields: IncidentReviewFields): string {
  if (!fields) return 'Not provided'
  if (fields.incidentAddressUnknown) {
    return formatUnknownIncidentReviewLocation(fields)
  }
  const street = fields.incidentStreet?.trim()
  const suburb = fields.incidentSuburb?.trim()
  const state = fields.incidentState?.trim()
  const pc = fields.incidentPostcode?.trim()
  const statePc = [state, pc].filter(Boolean).join(' ').trim()
  const line1 = street
  const line2 = [suburb, statePc].filter(Boolean).join(', ')
  const combined = [line1, line2].filter(Boolean).join(', ')
  return combined || 'Not provided'
}

function formatInsuredDriverNameRailSummary(
  first: string | null | undefined,
  last: string | null | undefined
): string | undefined {
  const name = [first?.trim(), last?.trim()].filter(Boolean).join(' ')
  if (!name) return undefined
  return formatClaimDescriptionRailSummary(name)
}

/**
 * Progress rail line for the multi-vehicle “driver at collision” step (from draft fields).
 */
export function formatMultiVehicleCollisionRailSummary(input: {
  multiVehicleDriverCaptureMode?: 'none_unattended' | 'primary' | 'listed' | 'manual' | null
  multiVehicleAttendedAtCollision?: boolean | null
  multiVehicleDriverFirstName?: string | null
  multiVehicleDriverLastName?: string | null
} | null | undefined): string | undefined {
  if (!input) return undefined
  if (
    input.multiVehicleDriverCaptureMode === 'none_unattended' ||
    input.multiVehicleAttendedAtCollision === false
  ) {
    return 'Vehicle unattended'
  }
  const name = formatInsuredDriverNameRailSummary(
    input.multiVehicleDriverFirstName,
    input.multiVehicleDriverLastName
  )
  if (name) return name
  switch (input.multiVehicleDriverCaptureMode) {
    case 'primary':
      return 'Primary driver'
    case 'listed':
      return 'Listed driver'
    case 'manual':
      return 'Not listed on policy'
    default:
      return undefined
  }
}

/** Progress rail line for the multi-vehicle “other driver” step (from draft fields). */
export function formatMultiVehicleOtherPartyRailSummary(
  input: MultiVehicleOtherPartyRailSummaryInput | null | undefined
): string | undefined {
  if (!input || input.multiVehicleOtherPartyDetailsKnown === null || input.multiVehicleOtherPartyDetailsKnown === undefined) {
    return undefined
  }
  if (!input.multiVehicleOtherPartyDetailsKnown) {
    return 'No details'
  }
  const names = ([1, 2, 3] as const)
    .map((index) => readOtherPartyDriverFromDraft(input, index).name.trim())
    .filter(Boolean)
  if (names.length === 0) return undefined
  return formatClaimDescriptionRailSummary(names.join(', '))
}

/** Progress rail line for single-vehicle “what did you collide with?” (from draft fields). */
export function formatSingleVehicleCollisionRailSummary(input: {
  singleVehicleCollisionTarget?: SingleVehicleCollisionTargetId | null
  singleVehicleCollisionTargetOtherText?: string | null
} | null | undefined): string | undefined {
  if (!input?.singleVehicleCollisionTarget) return undefined
  switch (input.singleVehicleCollisionTarget) {
    case 'animal':
      return 'Animal'
    case 'object':
      return 'Object'
    case 'third_party_property':
      return 'Third party property'
    case 'other': {
      const t = input.singleVehicleCollisionTargetOtherText?.trim()
      if (!t) return 'Other'
      return t.length > 40 ? `${t.slice(0, 37)}…` : t
    }
    default:
      return undefined
  }
}


type ClaimFlowTimelineEntry = {
  path: string
  id: string
  title: string
  summary?: string
}

/** Desktop progress rail: history-only (completed steps before the current landing page). */
function isSubmittedPath(path: string): boolean {
  return path.endsWith('/submitted')
}

function toCompletedHistory(entries: ClaimFlowTimelineEntry[]): ClaimFlowStepView[] {
  return entries.map(({ path: _path, id, title, summary }) => ({
    id,
    title,
    summary,
    status: 'complete',
  }))
}

/** Completed steps only — the current landing page never appears as a bubble. */
function claimFlowHistoryFromTimeline(
  timeline: ClaimFlowTimelineEntry[],
  pathname: string
): ClaimFlowStepView[] {
  const path = normalizePath(pathname)
  const idx = timeline.findIndex((entry) => entry.path === path)
  if (idx < 0) {
    return isSubmittedPath(path) ? toCompletedHistory(timeline) : []
  }
  return toCompletedHistory(timeline.slice(0, idx))
}

function towingAnswerSummary(answer: 'yes' | 'no' | null | undefined): string | undefined {
  if (answer === 'yes') return 'Towing required: Yes'
  if (answer === 'no') return 'Towing required: No'
  return undefined
}

function windscreenChipRailSummary(isSmallChip: boolean | null | undefined): string | undefined {
  if (isSmallChip === true) return 'Small chip: Yes'
  if (isSmallChip === false) return 'Small chip: No'
  return undefined
}

function windscreenRepairRailSummary(
  repairOption: 'trusted' | 'own' | null | undefined,
  smallChip: boolean | null | undefined,
  replacedAlready: boolean | null | undefined
): string | undefined {
  if (replacedAlready === true) return 'Already replaced'
  if (repairOption === 'trusted') return 'Trusted repairer'
  if (repairOption === 'own') return 'Own repairer'
  if (smallChip === true) return undefined
  return undefined
}

/** Driver-step line for weather / fire / theft (parked question) with multi-vehicle fallback. */
export function formatOtherBranchDriverRailSummary(
  snapshot: Pick<
    ClaimFlowSnapshot,
    | 'otherClaimSubtypeId'
    | 'multiVehicleCollisionSummary'
    | 'weatherVehicleParkedAtIncident'
    | 'fireVehicleParkedAtIncident'
    | 'theftVehicleParkedAtIncident'
  >
): string | undefined {
  if (snapshot.otherClaimSubtypeId === 'weather') {
    if (snapshot.weatherVehicleParkedAtIncident === true) return 'Vehicle parked at incident'
  }
  if (snapshot.otherClaimSubtypeId === 'fire') {
    if (snapshot.fireVehicleParkedAtIncident === true) return 'Vehicle parked at incident'
  }
  if (snapshot.otherClaimSubtypeId === 'theft') {
    if (snapshot.theftVehicleParkedAtIncident === true) return 'Vehicle parked at incident'
  }
  const fromProp = snapshot.multiVehicleCollisionSummary?.trim()
  return fromProp || undefined
}

type RailContext = {
  policyLine?: string
  incidentLine?: string
  gstLine?: string
  subtypeSummaryLine?: string
  driverLine?: string
  otherPartyLine?: string
  describeLine?: string
  singleVehicleDescribeLine?: string
  weatherDescribeStepSummary?: string
  fireDescribeStepSummary?: string
  theftDescribeStepSummary?: string
  singleVehicleCollisionLine?: string
  windscreenDescribeLine?: string
  windscreenFurtherLine?: string
  windscreenRepairLine?: string
  towingLine?: string
}

function buildRailContext(snapshot: ClaimFlowSnapshot): RailContext {
  const describeRailSummary = formatMultiVehicleDescribeRailSummary(snapshot)

  return {
    policyLine: snapshot.policySummary?.trim() || undefined,
    incidentLine: snapshot.incidentSummary?.trim() || undefined,
    gstLine:
      snapshot.claimEntryBranch === 'windscreen'
        ? windscreenGstRailSummary(
            snapshot.windscreenGstRegistered ?? null,
            snapshot.windscreenAbn ?? null,
            snapshot.windscreenItcEntitled ?? null,
            snapshot.windscreenItcPercentage ?? null
          )
        : undefined,
    subtypeSummaryLine: formatOtherClaimSubtypeSummary(snapshot.otherClaimSubtypeId ?? undefined),
    driverLine: formatOtherBranchDriverRailSummary(snapshot),
    otherPartyLine: snapshot.multiVehicleOtherPartySummary?.trim() || undefined,
    describeLine: formatMultiVehicleDescribeRailSummary(snapshot),
    singleVehicleDescribeLine: formatSingleVehicleDescribeRailSummary(snapshot),
    weatherDescribeStepSummary: describeRailSummary,
    fireDescribeStepSummary: describeRailSummary,
    theftDescribeStepSummary: describeRailSummary,
    singleVehicleCollisionLine: snapshot.singleVehicleCollisionSummary?.trim() || undefined,
    windscreenDescribeLine: formatClaimDescriptionRailSummary(snapshot.windscreenDescription ?? null),
    windscreenFurtherLine: windscreenChipRailSummary(snapshot.windscreenSmallChip ?? null),
    windscreenRepairLine: windscreenRepairRailSummary(
      snapshot.windscreenRepairOption ?? null,
      snapshot.windscreenSmallChip ?? null,
      snapshot.windscreenReplacedAlready ?? null
    ),
    towingLine: towingAnswerSummary(snapshot.towingAnswer ?? null),
  }
}

const STEP_START = { id: 'start', title: 'Before you start' }
const STEP_TYPE = { id: 'claim-type', title: 'What happened?' }
const STEP_INCIDENT = { id: 'incident-when-where', title: 'When & where' }
const STEP_OTHER_SUBTYPE = { id: 'other-claim-subtype', title: 'Type of claim' }
const STEP_OTHER_SINGLE_VEHICLE_DRIVER = { id: 'other-single-vehicle-driver', title: 'Driver at incident' }
const STEP_OTHER_SINGLE_VEHICLE_COLLISION = {
  id: 'other-single-vehicle-collision',
  title: 'What you collided with',
}
const STEP_OTHER_SINGLE_VEHICLE_DESCRIBE = { id: 'other-single-vehicle-describe', title: 'Incident & damage' }
const STEP_OTHER_SINGLE_VEHICLE_CONFIRM = { id: 'other-single-vehicle-confirm', title: 'Confirm your details' }
const STEP_OTHER_MULTI_VEHICLE_DRIVER = { id: 'other-multi-vehicle-driver', title: 'Driver at collision' }
const STEP_OTHER_MULTI_VEHICLE_OTHER_PARTY = { id: 'other-multi-vehicle-other-party', title: 'Other driver' }
const STEP_OTHER_MULTI_VEHICLE_DESCRIBE = { id: 'other-multi-vehicle-describe', title: 'Incident & damage' }
const STEP_OTHER_MULTI_VEHICLE_CONFIRM = { id: 'other-multi-vehicle-confirm', title: 'Confirm your details' }
const STEP_OTHER_WEATHER_DRIVER = { id: 'other-weather-driver', title: 'Driver at incident' }
const STEP_OTHER_WEATHER_DESCRIBE = { id: 'other-weather-describe', title: 'What happened' }
const STEP_OTHER_WEATHER_CONFIRM = { id: 'other-weather-confirm', title: 'Confirm your details' }
const STEP_OTHER_FIRE_DRIVER = { id: 'other-fire-driver', title: 'Driver at incident' }
const STEP_OTHER_FIRE_DESCRIBE = { id: 'other-fire-describe', title: 'What happened' }
const STEP_OTHER_FIRE_CONFIRM = { id: 'other-fire-confirm', title: 'Confirm your details' }
const STEP_OTHER_THEFT_DRIVER = { id: 'other-theft-driver', title: 'Driver at incident' }
const STEP_OTHER_THEFT_DESCRIBE = { id: 'other-theft-describe', title: 'What happened' }
const STEP_OTHER_THEFT_CONFIRM = { id: 'other-theft-confirm', title: 'Confirm your details' }
const STEP_OTHER_MALICIOUS_DAMAGE_DESCRIBE = { id: 'other-malicious-damage-describe', title: 'What happened' }
const STEP_OTHER_MALICIOUS_DAMAGE_CONFIRM = {
  id: 'other-malicious-damage-confirm',
  title: 'Confirm your details',
}
const STEP_OTHER_OTHER_LOSS_DESCRIBE = { id: 'other-other-loss-describe', title: 'What happened' }
const STEP_OTHER_OTHER_LOSS_CONFIRM = { id: 'other-other-loss-confirm', title: 'Confirm your details' }
const STEP_WINDSCREEN_DESCRIBE = { id: 'windscreen-describe', title: 'Describe what happened' }
const STEP_WINDSCREEN_FURTHER = { id: 'windscreen-further', title: 'Add further detail' }
const STEP_WINDSCREEN_REPAIR = { id: 'windscreen-repair', title: 'Choose your repair option' }
const STEP_WINDSCREEN_CONFIRM = { id: 'windscreen-confirm', title: 'Confirm your details' }
const STEP_TOWING = { id: 'towing', title: 'Towing' }
const STEP_DETAILS = { id: 'claim-details', title: 'Further details' }

function entry(path: string, step: { id: string; title: string }, summary?: string): ClaimFlowTimelineEntry {
  return { path, ...step, summary }
}

function otherPrefix(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
    entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_OTHER_CLAIM),
    entry(CLAIM_FLOW_PATH.otherClaimSubtype, STEP_OTHER_SUBTYPE, ctx.subtypeSummaryLine),
    entry(CLAIM_FLOW_PATH.incident, STEP_INCIDENT, ctx.incidentLine),
  ]
}

function windscreenPrefix(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
    entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_WINDSCREEN),
    entry(CLAIM_FLOW_PATH.incident, STEP_INCIDENT, ctx.incidentLine),
  ]
}

function windscreenTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...windscreenPrefix(ctx),
    entry(CLAIM_FLOW_PATH.windscreen, STEP_WINDSCREEN_DESCRIBE, ctx.windscreenDescribeLine),
    entry(CLAIM_FLOW_PATH.windscreenDetail, STEP_WINDSCREEN_FURTHER, ctx.windscreenFurtherLine),
    entry(CLAIM_FLOW_PATH.windscreenRepairOption, STEP_WINDSCREEN_REPAIR, ctx.windscreenRepairLine),
    entry(CLAIM_FLOW_PATH.windscreenReview, STEP_WINDSCREEN_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.windscreenSubmitted, STEP_WINDSCREEN_CONFIRM, ctx.gstLine),
  ]
}

function legacyOtherTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.towing, STEP_TOWING, ctx.towingLine),
    entry(CLAIM_FLOW_PATH.details, STEP_DETAILS),
  ]
}

function multiVehicleTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherMultiVehicleDriver, STEP_OTHER_MULTI_VEHICLE_DRIVER, ctx.driverLine),
    entry(CLAIM_FLOW_PATH.otherMultiVehicleOtherParty, STEP_OTHER_MULTI_VEHICLE_OTHER_PARTY, ctx.otherPartyLine),
    entry(CLAIM_FLOW_PATH.otherMultiVehicleDescribe, STEP_OTHER_MULTI_VEHICLE_DESCRIBE, ctx.describeLine),
    entry(CLAIM_FLOW_PATH.otherMultiVehicleReview, STEP_OTHER_MULTI_VEHICLE_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherMultiVehicleSubmitted, STEP_OTHER_MULTI_VEHICLE_CONFIRM, ctx.gstLine),
  ]
}

function singleVehicleTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherSingleVehicleDriver, STEP_OTHER_SINGLE_VEHICLE_DRIVER, ctx.driverLine),
    entry(
      CLAIM_FLOW_PATH.otherSingleVehicleCollisionWith,
      STEP_OTHER_SINGLE_VEHICLE_COLLISION,
      ctx.singleVehicleCollisionLine
    ),
    entry(
      CLAIM_FLOW_PATH.otherSingleVehicleDescribe,
      STEP_OTHER_SINGLE_VEHICLE_DESCRIBE,
      ctx.singleVehicleDescribeLine
    ),
    entry(CLAIM_FLOW_PATH.otherSingleVehicleReview, STEP_OTHER_SINGLE_VEHICLE_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherSingleVehicleSubmitted, STEP_OTHER_SINGLE_VEHICLE_CONFIRM, ctx.gstLine),
  ]
}

function weatherTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherWeatherDriver, STEP_OTHER_WEATHER_DRIVER, ctx.driverLine),
    entry(CLAIM_FLOW_PATH.otherWeatherDescribe, STEP_OTHER_WEATHER_DESCRIBE, ctx.weatherDescribeStepSummary),
    entry(CLAIM_FLOW_PATH.otherWeatherReview, STEP_OTHER_WEATHER_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherWeatherSubmitted, STEP_OTHER_WEATHER_CONFIRM, ctx.gstLine),
  ]
}

function fireTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherFireDriver, STEP_OTHER_FIRE_DRIVER, ctx.driverLine),
    entry(CLAIM_FLOW_PATH.otherFireDescribe, STEP_OTHER_FIRE_DESCRIBE, ctx.fireDescribeStepSummary),
    entry(CLAIM_FLOW_PATH.otherFireReview, STEP_OTHER_FIRE_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherFireSubmitted, STEP_OTHER_FIRE_CONFIRM, ctx.gstLine),
  ]
}

function theftTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherTheftDriver, STEP_OTHER_THEFT_DRIVER, ctx.driverLine),
    entry(CLAIM_FLOW_PATH.otherTheftDescribe, STEP_OTHER_THEFT_DESCRIBE, ctx.theftDescribeStepSummary),
    entry(CLAIM_FLOW_PATH.otherTheftReview, STEP_OTHER_THEFT_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherTheftSubmitted, STEP_OTHER_THEFT_CONFIRM, ctx.gstLine),
  ]
}

function maliciousDamageTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(
      CLAIM_FLOW_PATH.otherMaliciousDamageDescribe,
      STEP_OTHER_MALICIOUS_DAMAGE_DESCRIBE,
      ctx.fireDescribeStepSummary
    ),
    entry(CLAIM_FLOW_PATH.otherMaliciousDamageReview, STEP_OTHER_MALICIOUS_DAMAGE_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherMaliciousDamageSubmitted, STEP_OTHER_MALICIOUS_DAMAGE_CONFIRM, ctx.gstLine),
  ]
}

function otherLossTimeline(ctx: RailContext): ClaimFlowTimelineEntry[] {
  return [
    ...otherPrefix(ctx),
    entry(CLAIM_FLOW_PATH.otherOtherLossDescribe, STEP_OTHER_OTHER_LOSS_DESCRIBE, ctx.fireDescribeStepSummary),
    entry(CLAIM_FLOW_PATH.otherOtherLossReview, STEP_OTHER_OTHER_LOSS_CONFIRM, ctx.gstLine),
    entry(CLAIM_FLOW_PATH.otherOtherLossSubmitted, STEP_OTHER_OTHER_LOSS_CONFIRM, ctx.gstLine),
  ]
}

function resolveOtherTimeline(ctx: RailContext, snapshot: ClaimFlowSnapshot): ClaimFlowTimelineEntry[] {
  const subtype = snapshot.otherClaimSubtypeId
  if (subtype === 'multi-vehicle-collision') return multiVehicleTimeline(ctx)
  if (subtype === 'single-vehicle') return singleVehicleTimeline(ctx)
  if (subtype === 'weather') return weatherTimeline(ctx)
  if (subtype === 'fire') return fireTimeline(ctx)
  if (subtype === 'theft') return theftTimeline(ctx)
  if (subtype === 'malicious-damage') return maliciousDamageTimeline(ctx)
  if (subtype === 'other-loss') return otherLossTimeline(ctx)
  return legacyOtherTimeline(ctx)
}

/** Full ordered timeline for the active branch (used to slice history for the current pathname). */
function buildClaimFlowTimeline(snapshot: ClaimFlowSnapshot): ClaimFlowTimelineEntry[] {
  const path = normalizePath(snapshot.pathname)
  const ctx = buildRailContext(snapshot)

  if (path === CLAIM_FLOW_PATH.start) {
    return [entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine)]
  }

  if (path === CLAIM_FLOW_PATH.selectType) {
    return [
      entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
      entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE),
    ]
  }

  if (path === CLAIM_FLOW_PATH.otherClaimSubtype) {
    return [
      entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
      entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_OTHER_CLAIM),
      entry(CLAIM_FLOW_PATH.otherClaimSubtype, STEP_OTHER_SUBTYPE, ctx.subtypeSummaryLine),
    ]
  }

  if (snapshot.claimEntryBranch === 'windscreen' || path.startsWith('/claims/windscreen')) {
    if (path === CLAIM_FLOW_PATH.windscreenGst) {
      return [
        ...windscreenPrefix(ctx),
        entry(CLAIM_FLOW_PATH.windscreenGst, STEP_WINDSCREEN_DESCRIBE, ctx.windscreenDescribeLine),
        entry(CLAIM_FLOW_PATH.windscreenDetail, STEP_WINDSCREEN_FURTHER, ctx.windscreenFurtherLine),
        entry(CLAIM_FLOW_PATH.windscreenRepairOption, STEP_WINDSCREEN_REPAIR, ctx.windscreenRepairLine),
        entry(CLAIM_FLOW_PATH.windscreenReview, STEP_WINDSCREEN_CONFIRM, ctx.gstLine),
      ]
    }
    return windscreenTimeline(ctx)
  }

  if (path === CLAIM_FLOW_PATH.otherGst) {
    const subtype = snapshot.otherClaimSubtypeId
    if (subtype === 'single-vehicle') return singleVehicleTimeline(ctx)
    return legacyOtherTimeline(ctx)
  }

  if (path === CLAIM_FLOW_PATH.towing && snapshot.otherClaimSubtypeId === 'single-vehicle') {
    return singleVehicleTimeline(ctx)
  }

  if (path === CLAIM_FLOW_PATH.details) {
    return resolveOtherTimeline(ctx, snapshot)
  }

  return resolveOtherTimeline(ctx, snapshot)
}

/**
 * Desktop progress rail: only steps the user has already finished (history), never upcoming preview.
 */
export function deriveClaimFlowSteps(snapshot: ClaimFlowSnapshot): ClaimFlowStepView[] {
  const path = normalizePath(snapshot.pathname)
  const ctx = buildRailContext(snapshot)

  // Explicit early pages — never infer a future branch (no "When & where" on select-type).
  if (path === CLAIM_FLOW_PATH.start) {
    return []
  }
  if (path === CLAIM_FLOW_PATH.selectType) {
    return toCompletedHistory([entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine)])
  }
  if (path === CLAIM_FLOW_PATH.otherClaimSubtype) {
    return toCompletedHistory([
      entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
      entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_OTHER_CLAIM),
    ])
  }
  if (path === CLAIM_FLOW_PATH.incident) {
    if (snapshot.claimEntryBranch === 'windscreen') {
      return toCompletedHistory([
        entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
        entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_WINDSCREEN),
      ])
    }
    if (snapshot.claimEntryBranch === 'other') {
      return toCompletedHistory([
        entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
        entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE, CLAIM_FLOW_SUMMARY_OTHER_CLAIM),
        entry(CLAIM_FLOW_PATH.otherClaimSubtype, STEP_OTHER_SUBTYPE, ctx.subtypeSummaryLine),
      ])
    }
    return toCompletedHistory([
      entry(CLAIM_FLOW_PATH.start, STEP_START, ctx.policyLine),
      entry(CLAIM_FLOW_PATH.selectType, STEP_TYPE),
    ])
  }

  return claimFlowHistoryFromTimeline(buildClaimFlowTimeline(snapshot), snapshot.pathname)
}
